import { http } from "./http";

// ─── Types ────────────────────────────────────────────────────────────────────

export type VisitLabResultStatus = "ok" | "warn" | "bad" | "unknown";
export type OrderStatus = "PENDING" | "PARTIAL" | "RESULTED" | "CANCELLED";

export interface VisitLabResultSummary {
  total: number;
  ok: number;
  warn: number;
  bad: number;
  unknown: number;
}

export interface VisitLabOrderSummary {
  orderId: string;
  appointmentId: string | null;
  visitSourceRef?: string | null;
  appointmentType: "IN_PERSON" | "TELEMED" | "FOLLOWUP" | "OPD";
  scheduledAt: string;
  orderedAt: string;
  collectedAt: string | null;
  orderStatus: OrderStatus;
  labName: string | null;
  clinicalNote?: string | null;
  resultSummary: VisitLabResultSummary;
  overallStatus: VisitLabResultStatus | "pending";
  doctorName: string | null;
  hasSensitiveLab?: boolean;
  requiresInPersonPickup?: boolean;
  pickupInstruction?: string | null;
}

export interface VisitLabResultItem {
  testCode: string;
  testName: string;
  category: string;
  valueNumeric: number | null;
  valueText: string | null;
  unit: string | null;
  status: VisitLabResultStatus | null;
  refMin: number | null;
  refMax: number | null;
  warnMin: number | null;
  warnMax: number | null;
  note: string | null;
  takenAt: string | null;
  meaning: string | null;
  isSensitive?: boolean;
  requiresInPersonPickup?: boolean;
}

export interface VisitLabOrderDetail extends VisitLabOrderSummary {
  clinicalNote: string | null;
  results: VisitLabResultItem[];
}

export interface ListVisitLabResponse {
  items: VisitLabOrderSummary[];
  hasMore: boolean;
}

// ─── API Client ───────────────────────────────────────────────────────────────

export const visitLabApi = {
  /**
   * รายการผล Lab จากการมาตรวจ (newest first, cursor-based pagination)
   */
  async list(params?: {
    limit?: number;
    cursor?: string;
    appointmentId?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<ListVisitLabResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.appointmentId) query.set("appointmentId", params.appointmentId);
    if (params?.fromDate) query.set("fromDate", params.fromDate);
    if (params?.toDate) query.set("toDate", params.toDate);
    const qs = query.toString();
    const { data } = await http.get<ListVisitLabResponse>(
      `/public/visit-lab${qs ? `?${qs}` : ""}`,
    );
    return data;
  },

  /**
   * รายละเอียดผล Lab พร้อม result ทุกตัว
   */
  async getDetail(orderId: string): Promise<VisitLabOrderDetail> {
    const { data } = await http.get<VisitLabOrderDetail>(
      `/public/visit-lab/${orderId}`,
    );
    return data;
  },
};

